import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { NewSidebareCommercial } from '../../Views/NewSideBareCommercial';
import '../../Views/Magasin/commande/commande.css';
import './CommandeCommercial.css';
import AfficheMagasinsService from '../Magasin/AfficheMagasinsService';
import { MagasinInfo } from '../../Modeles/MagasinModel';
import QrPaymentModal from '../../components/QrPaymentModal';
import OverlayLoader from '../../components/OverlayLoader';

interface PanierItem {
  id: number;
  IdArticle: number;
  RefArticle: string;
  Designation: string;
  Image: string;
  price: string;
  quantity: number;
  NomMagasin: string;
}

interface SocieteLivraison {
  id: number;
  NomSociete: string;
  AdresseSociete?: string;
  Prix?: number;
  Status?: string;
}

const CommandeCommercialService = () => {
  const navigate = useNavigate();
  const CommercialId = localStorage.getItem('CommercialId') ?? '';
  const CommercialName = localStorage.getItem('CommercialName') ?? '';
  const MagasinName = localStorage.getItem('MagasinName') ?? '';
  const SoldeCommercial = localStorage.getItem('SoldeCommercial');
  const soldeValue = SoldeCommercial ? parseFloat(SoldeCommercial) : 0;

  // Panier items from API
  const [panierItems, setPanierItems] = useState<PanierItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Order form
  const AdresseDefault = localStorage.getItem('adresse') ?? '';
  const [nomClient, setNomClient] = useState(CommercialName);
  const [adresse, setAdresse] = useState(AdresseDefault);
  const [remarque, setRemarque] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [modeLivraison, setModeLivraison] = useState<'magasin' | 'societe'>('magasin');
  const [selectedMagasinId, setSelectedMagasinId] = useState<any>(null);
  const [selectedSocieteId, setSelectedSocieteId] = useState<number | null>(null);

  // Magasins & Sociétés
  const [allMagasins, setAllMagasins] = useState<MagasinInfo[]>([]);
  const [societes, setSocietes] = useState<SocieteLivraison[]>([]);
  const [loadingMagasins, setLoadingMagasins] = useState(false);

  // UI state
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState<'success' | 'error'>('success');
  const [submitting, setSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [commandeIdForPayment, setCommandeIdForPayment] = useState('');

  // Timer
  const [timerStr, setTimerStr] = useState('');

  // Fetch panier from API
  const fetchPanier = async () => {
    if (!CommercialId) return;
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_PHP_APP_URL}/panier/visiteur/${CommercialId}/commercials`
      );
      setPanierItems(Array.isArray(data) ? data : []);
    } catch { setPanierItems([]); }
    finally { setLoading(false); }
  };

  // Fetch magasins — only those that accept colis (ReceptionClois == 1)
  const fetchMagasins = async () => {
    setLoadingMagasins(true);
    try {
      const res = await AfficheMagasinsService().getMagasin();
      const filtered = res.data.filter(
        (m: MagasinInfo) => m.NomMagasin !== MagasinName && (m.ReceptionClois == 1 || m.ReceptionClois === '1')
      );
      setAllMagasins(filtered);
    } catch { }
    finally { setLoadingMagasins(false); }
  };

  // Fetch sociétés de livraison
  const fetchSocietes = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_PHP_APP_URL}/admin/societe-livraison`);
      const active = (Array.isArray(data) ? data : []).filter((s: any) => s.Status === 'active' || !s.Status);
      setSocietes(active);
    } catch { setSocietes([]); }
  };

  useEffect(() => { fetchPanier(); fetchMagasins(); fetchSocietes(); }, []);

  // Timer counting UP from StartTimer — elapsed time since first article added
  useEffect(() => {
    if (panierItems.length === 0) { setTimerStr(''); return; }

    // Find the earliest StartTimer (when first article was added)
    let earliestStart = Infinity;
    panierItems.forEach((item: any) => {
      if (item.StartTimer) {
        const t = new Date(item.StartTimer).getTime();
        if (t < earliestStart) earliestStart = t;
      }
    });
    if (earliestStart === Infinity) { setTimerStr(''); return; }

    const updateTimer = () => {
      const diff = Date.now() - earliestStart;
      if (diff < 0) { setTimerStr('00:00:00'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimerStr(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [panierItems]);

  // Calculate total
  const calcTotal = () => {
    return panierItems.reduce((sum, item) => {
      return sum + (parseFloat(String(item.price).replace(',', '.')) || 0) * (item.quantity || 1);
    }, 0);
  };

  const total = calcTotal();
  const selectedSociete = societes.find(s => s.id === selectedSocieteId);
  const fraisLivraison = modeLivraison === 'societe' && selectedSociete?.Prix 
    ? parseFloat(String(selectedSociete.Prix)) || 0 
    : modeLivraison === 'magasin' 
      ? 100 
      : 0;
  const totalFinal = total + fraisLivraison;

  // Show popup helper
  const showAlert = (msg: string, type: 'success' | 'error') => {
    setErreur(type === 'error' ? msg : '');
    setMessage(type === 'success' ? msg : '');
    setPopupType(type);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 3500);
  };

  // Delete single item
  const handleDelete = async (id: number) => {
    try {
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/panier/delete`, { id });
      setPanierItems(prev => prev.filter(i => i.id !== id));
      window.dispatchEvent(new Event('panier-updated'));
    } catch { }
  };

  // Submit order
  const handleSubmit = async () => {
    if (panierItems.length === 0) {
      showAlert('Votre panier est vide.', 'error'); return;
    }
    if (!paymentType) {
      showAlert('Veuillez sélectionner une méthode de paiement.', 'error'); return;
    }
    if (paymentType === 'solde' && totalFinal > soldeValue) {
      showAlert(`Solde insuffisant (${soldeValue.toFixed(2)} MAD). Veuillez choisir une autre méthode.`, 'error'); return;
    }
    if (modeLivraison === 'magasin' && !selectedMagasinId) {
      showAlert('Veuillez choisir un magasin de retrait.', 'error'); return;
    }
    if (modeLivraison === 'societe' && !selectedSocieteId) {
      showAlert('Veuillez choisir une société de livraison.', 'error'); return;
    }
    if (!adresse || !adresse.trim()) {
      showAlert('Veuillez saisir une adresse de livraison.', 'error'); return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('NomClient', nomClient);
      formData.append('adress', adresse);
      formData.append('Remarque', remarque);
      const resolvedMagName = MagasinName || localStorage.getItem('CommercialName') || 'Commercial';
      formData.append('NomMagasin', resolvedMagName);
      formData.append('IdMagasin', CommercialId);
      formData.append('SousClient', CommercialId);
      // MethodPayment and StatutPayment mapping
      const methodPaymentVal = paymentType === 'solde' ? 'par solde' : 'A la livraison';
      const statutPaymentVal = paymentType === 'solde' ? 'payé' : 'non payé';
      formData.append('MethodPayment', methodPaymentVal);
      formData.append('StatutPayment', statutPaymentVal);

      formData.append('TypeUtilisateur', 'commercial');
      formData.append('ModeLivraison', modeLivraison);

      formData.append('FraisLivraison', fraisLivraison.toString());
      formData.append('TotalHT', totalFinal.toString());
      formData.append('TotalTTC', totalFinal.toString());

      if (modeLivraison === 'societe' && selectedSociete) {
        formData.append('SocieteLivraison', selectedSociete.NomSociete);
        formData.append('MagasinRetrait', selectedSociete.NomSociete);
        formData.append('AdresseRetreit', selectedSociete.AdresseSociete || '');
      }
      if (modeLivraison === 'magasin' && selectedMagasinId) {
        const mag = allMagasins.find(m => m.IdMagasin === selectedMagasinId);
        formData.append('MagasinRetrait', mag?.NomMagasin || '');
        formData.append('AdresseRetreit', mag?.adresse_de_siege || '');
      }

      panierItems.forEach((item, i) => {
        formData.append(`detail[${i}][refArticle]`, item.RefArticle);
        formData.append(`detail[${i}][designation]`, item.Designation);
        formData.append(`detail[${i}][prix]`, String(item.price));
        formData.append(`detail[${i}][quantity]`, String(item.quantity));
        formData.append(`detail[${i}][NomMagasin]`, item.NomMagasin || '');
        formData.append(`detail[${i}][IdArticle]`, String(item.IdArticle));
      });

      const res = await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/enregistrerCommande`, formData);
      const idBl = res.data?.IdBl || res.data?.idbl;

      if (idBl) {
        try {
          await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/sendArticleCommande/${idBl}`, {
            DataTable: panierItems.map(i => ({
              refArticle: i.RefArticle,
              designation: i.Designation,
              prix: i.price,
              quantity: i.quantity,
              NomMagasin: i.NomMagasin,
              IdArticle: i.IdArticle
            }))
          }, { headers: { 'Content-Type': 'application/json' } });
        } catch (soapArtErr) {
          console.error("Error sending commercial articles to SOAP:", soapArtErr);
        }
      }

      // Decrease from solde if paid by solde
      if (paymentType === 'solde') {
        const newSolde = soldeValue - totalFinal;
        const magasinConnecte = localStorage.getItem('magasin_connecte') || localStorage.getItem('MagasinId');
        await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/commercial/${CommercialId}/update-solde`, {
          amount: newSolde,
          idMagasin: magasinConnecte
        });
        localStorage.setItem('SoldeCommercial', newSolde.toFixed(2));
      }

      // Clear panier
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/panier/delete/all/${CommercialId}`);
      window.dispatchEvent(new Event('panier-updated'));

      showAlert('Commande effectuée avec succès !' + (idBl ? ` (N° BL : ${idBl})` : ''), 'success');
      
      if (paymentType === 'normal') {
        setCommandeIdForPayment(String(idBl || res.data?.IdCom || ""));
        setShowPayment(true);
      } else {
        setTimeout(() => navigate(`/commercials/${CommercialId}`), 2000);
      }
    } catch (err: any) {
      console.error("Erreur commande commercial:", err);
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || 'Erreur lors de la commande. Veuillez réessayer.';
      showAlert(errMsg, 'error');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <NewSidebareCommercial />
      <div className="cc-root">
        {/* Popup */}
        {showPopup && (
          <div className="cc-popup-overlay">
            <div className="cc-popup">
              <div className={`cc-popup-icon cc-popup-icon--${popupType}`}>
                <i className={`bi ${popupType === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`} />
              </div>
              <h4>{popupType === 'success' ? 'Succès !' : 'Attention !'}</h4>
              <p>{popupType === 'success' ? message : erreur}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="cc-header">
          <button className="cc-back" onClick={() => window.history.back()}>
            <i className="bi bi-arrow-left" />
          </button>
          <div>
            <h1 className="cc-title">Finaliser la commande</h1>
            <p className="cc-subtitle">Vérifiez vos articles et complétez votre commande</p>
          </div>
          {timerStr && (
            <div className={`cc-timer ${timerStr === 'Expiré' ? 'cc-timer--expired' : ''}`}>
              <i className="bi bi-clock-history" />
              <span>{timerStr === 'Expiré' ? 'Panier expiré' : `Panier actif depuis ${timerStr}`}</span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="cc-loading"><span className="cc-spinner" /> Chargement...</div>
        ) : panierItems.length === 0 ? (
          <div className="cc-empty">
            <i className="bi bi-cart-x" />
            <h3>Panier vide</h3>
            <p>Ajoutez des articles pour passer une commande</p>
          </div>
        ) : (
          <div className="cc-body">
            {/* Left: Articles */}
            <div className="cc-articles-section">
              <div className="cc-section-title">
                <i className="bi bi-box-seam" /> Articles ({panierItems.length})
              </div>
              <div className="cc-articles-list">
                {panierItems.map((item) => (
                  <div key={item.id} className="cc-article-card">
                    <img src={item.Image} alt={item.Designation} className="cc-article-img" onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/80x80?text=img'; }} />
                    <div className="cc-article-info">
                      <h4>{item.Designation}</h4>
                      <span className="cc-article-ref">Réf: {item.RefArticle}</span>
                      <span className="cc-article-mag">{item.NomMagasin}</span>
                    </div>
                    <div className="cc-article-right">
                      <span className="cc-article-qty">x{item.quantity}</span>
                      <span className="cc-article-price">{(parseFloat(String(item.price).replace(',', '.')) * item.quantity).toFixed(2)} MAD</span>
                      <button className="cc-article-del" onClick={() => handleDelete(item.id)}><i className="bi bi-trash3" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form + Summary */}
            <div className="cc-right-section">
              {/* Delivery */}
              <div className="cc-card">
                <div className="cc-section-title"><i className="bi bi-truck" /> Mode de livraison</div>
                <div className="cc-livraison-grid">
                  <button type="button" className={`cc-livraison-opt ${modeLivraison === 'magasin' ? 'cc-livraison-opt--active' : ''}`} onClick={() => { setModeLivraison('magasin'); setSelectedSocieteId(null); }}>
                    <i className="bi bi-shop" />
                    <span>Magasin</span>
                    <small>Retrait en boutique</small>
                    {modeLivraison === 'magasin' && <span className="cc-check">✓</span>}
                  </button>
                  <button type="button" className={`cc-livraison-opt ${modeLivraison === 'societe' ? 'cc-livraison-opt--active' : ''}`} onClick={() => { setModeLivraison('societe'); setSelectedMagasinId(null); }}>
                    <i className="bi bi-truck" />
                    <span>Société de livraison</span>
                    <small>Livraison à domicile</small>
                    {modeLivraison === 'societe' && <span className="cc-check">✓</span>}
                  </button>
                </div>

                {modeLivraison === 'magasin' && (
                  <div className="cc-picker">
                    <label>Choisir le magasin de retrait</label>
                    {loadingMagasins ? <div className="cc-loading-sm"><span className="cc-spinner" /></div> : (
                      <div className="cc-magasin-list">
                        {allMagasins.map(mag => (
                          <button key={mag.IdMagasin} type="button" className={`cc-magasin-card ${selectedMagasinId === mag.IdMagasin ? 'cc-magasin-card--active' : ''}`} onClick={() => setSelectedMagasinId(mag.IdMagasin)}>
                            <i className="bi bi-shop" />
                            <div>
                              <strong>{mag.NomMagasin}</strong>
                              <small>{mag.adresse_de_siege}</small>
                            </div>
                            {selectedMagasinId === mag.IdMagasin && <span className="cc-check">✓</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {modeLivraison === 'societe' && (
                  <div className="cc-picker">
                    <label>Choisir la société de livraison</label>
                    <div className="cc-magasin-list">
                      {societes.length === 0 ? <p className="cc-no-data">Aucune société disponible</p> : societes.map(soc => (
                        <button key={soc.id} type="button" className={`cc-magasin-card ${selectedSocieteId === soc.id ? 'cc-magasin-card--active' : ''}`} onClick={() => setSelectedSocieteId(soc.id)}>
                          <i className="bi bi-truck" />
                          <div>
                            <strong>{soc.NomSociete}</strong>
                            {soc.AdresseSociete && <small>{soc.AdresseSociete}</small>}
                            {soc.Prix != null && <small>Frais: {soc.Prix} MAD</small>}
                          </div>
                          {selectedSocieteId === soc.id && <span className="cc-check">✓</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Client Name & Address */}
              <div className="cc-card">
                <div className="cc-section-title"><i className="bi bi-person" /> Informations client</div>
                <div className="cc-field">
                  <label><i className="bi bi-person-badge" /> Nom du client</label>
                  <input type="text" placeholder="Nom du client" value={nomClient} onChange={e => setNomClient(e.target.value)} />
                </div>
                <div className="cc-field">
                  <label><i className="bi bi-geo-alt" /> Adresse de livraison</label>
                  <input type="text" placeholder="ex: 12 Rue Mohammed V, Casablanca" value={adresse} onChange={e => setAdresse(e.target.value)} />
                </div>
                <div className="cc-field">
                  <label><i className="bi bi-chat-text" /> Remarque (optionnel)</label>
                  <input type="text" placeholder="Instructions spéciales..." value={remarque} onChange={e => setRemarque(e.target.value)} />
                </div>
              </div>

              {/* Payment */}
              <div className="cc-card">
                <div className="cc-section-title"><i className="bi bi-credit-card" /> Méthode de paiement</div>
                <div className="cc-payment-grid">
                  <label className={`cc-payment-opt ${paymentType === 'solde' ? 'cc-payment-opt--active' : ''}`}>
                    <input type="radio" name="payment" value="solde" checked={paymentType === 'solde'} onChange={e => setPaymentType(e.target.value)} />
                    <i className="bi bi-coin" />
                    <div>
                      <span>Payer avec votre solde</span>
                      <small>{soldeValue.toFixed(2)} MAD disponible</small>
                    </div>
                  </label>
                  <label className={`cc-payment-opt ${paymentType === 'normal' ? 'cc-payment-opt--active' : ''}`}>
                    <input type="radio" name="payment" value="normal" checked={paymentType === 'normal'} onChange={e => setPaymentType(e.target.value)} />
                    <i className="bi bi-cash-stack" />
                    <div>
                      <span>Paiement normal</span>
                      <small>À la livraison</small>
                    </div>
                  </label>
                </div>
                {paymentType === 'solde' && totalFinal > soldeValue && (
                  <div className="cc-solde-warning">
                    <i className="bi bi-exclamation-triangle-fill" />
                    Solde insuffisant ! Votre solde est de <strong>{soldeValue.toFixed(2)} MAD</strong> mais le total est de <strong>{totalFinal.toFixed(2)} MAD</strong>. Veuillez choisir le paiement normal.
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="cc-summary-card">
                <div className="cc-summary-title"><i className="bi bi-receipt" /> Récapitulatif</div>
                <div className="cc-summary-row"><span>Sous-total ({panierItems.length} articles)</span><span>{total.toFixed(2)} MAD</span></div>
                {fraisLivraison > 0 && <div className="cc-summary-row"><span>Frais de livraison</span><span>{fraisLivraison.toFixed(2)} MAD</span></div>}
                {fraisLivraison === 0 && <div className="cc-summary-row"><span>Livraison</span><span className="cc-free">Gratuite</span></div>}
                <div className="cc-summary-divider" />
                <div className="cc-summary-total"><span>Total</span><span>{totalFinal.toFixed(2)} MAD</span></div>
                <button className="cc-submit-btn" onClick={handleSubmit} disabled={submitting || panierItems.length === 0}>
                  {submitting ? <><span className="cc-spinner cc-spinner--sm" /> Envoi en cours...</> : <><i className="bi bi-bag-check" /> Confirmer la commande</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── QR Payment Modal ── */}
      {showPayment && (
        <QrPaymentModal
          commandeId={commandeIdForPayment}
          onBack={() => setShowPayment(false)}
          onClose={() => {
            setShowPayment(false);
            navigate(`/commercials/${CommercialId}`);
          }}
        />
      )}

      {/* ── Overlay Loader ── */}
      {submitting && <OverlayLoader message="Enregistrement de la commande..." />}
    </>
  );
};

export default CommandeCommercialService;
